/**
 * Upload image directly to Cloudinary from browser
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} - The public URL of the uploaded image
 */
export const uploadImageToCloudinary = async file => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
  const folder = process.env.REACT_APP_CLOUDINARY_FOLDER || 'company_logos';

  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary configuration is missing. Please set REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET in .env file');
  }

  // Validate file
  if (!file) {
    throw new Error('No file provided');
  }

  console.log('🖼️ [Cloudinary] Starting upload process...');
  console.log('📁 File details:', {
    name: file.name,
    type: file.type,
    size: `${(file.size / 1024).toFixed(2)} KB`,
  });

  // Validate file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    console.error('❌ [Cloudinary] Invalid file type:', file.type);
    throw new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image');
  }

  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    console.error('❌ [Cloudinary] File too large:', `${(file.size / 1024 / 1024).toFixed(2)} MB`);
    throw new Error('File size too large. Maximum size is 5MB');
  }

  // Create form data
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);
  formData.append('folder', folder);

  console.log('☁️ [Cloudinary] Uploading to:', `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`);
  console.log('📤 [Cloudinary] Upload preset:', uploadPreset);
  console.log('📂 [Cloudinary] Folder:', folder);

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json();
      console.error('❌ [Cloudinary] Upload failed:', error);
      throw new Error(error.error?.message || 'Failed to upload image to Cloudinary');
    }

    const data = await response.json();

    console.log('✅ [Cloudinary] Upload successful!');
    console.log('🔗 [Cloudinary] Image URL:', data.secure_url);
    console.log('📊 [Cloudinary] Response data:', {
      public_id: data.public_id,
      format: data.format,
      width: data.width,
      height: data.height,
      bytes: data.bytes,
    });

    // Return the secure URL (HTTPS)
    return data.secure_url;
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw error;
  }
};

/**
 * Generate a preview URL from a File object
 * @param {File} file - The image file
 * @returns {string} - The blob URL for preview
 */
export const generateImagePreview = file => {
  return URL.createObjectURL(file);
};

/**
 * Clean up preview URL to free memory
 * @param {string} previewUrl - The blob URL to revoke
 */
export const revokeImagePreview = previewUrl => {
  if (previewUrl && previewUrl.startsWith('blob:')) {
    URL.revokeObjectURL(previewUrl);
  }
};

/**
 * Delete an image from Cloudinary via server-side API
 * @param {string} imageUrl - The Cloudinary image URL to delete
 * @param {string} baseUrl - The API base URL (from apiBaseUrl())
 * @returns {Promise<Object>} - Response from deletion API
 */
export const deleteImageFromCloudinary = async (imageUrl, baseUrl) => {
  if (!imageUrl) {
    console.warn('⚠️ [Cloudinary] No image URL provided for deletion');
    return { success: false, message: 'No image URL provided' };
  }

  console.log('🗑️ [Cloudinary] Requesting deletion of:', imageUrl);
  console.log('🔧 [Cloudinary] Using API base URL:', baseUrl);

  try {
    const response = await fetch(`${baseUrl}/api/delete-cloudinary-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ imageUrl }),
    });

    console.log('📡 [Cloudinary] API response status:', response.status);

    const data = await response.json();

    if (response.ok && data.success) {
      console.log('✅ [Cloudinary] Image deleted successfully:', data);
      return data;
    } else {
      console.error('❌ [Cloudinary] Deletion failed:', data);
      return { success: false, ...data };
    }
  } catch (error) {
    console.error('❌ [Cloudinary] Deletion request failed:', error);
    return {
      success: false,
      error: 'Failed to delete image',
      message: error.message,
    };
  }
};
